import { Card, CardContent } from "@/components/ui/card";
import { PanelEmptyState } from "@/features/reader/side-panel/PanelEmptyState";
import { cn } from "@/lib/utils";
import { Document, Suggestion } from "@/types/document";
import { useMemo } from "react";

export const UserRemarksTab = ({
  document,
  selectedCriterionId,
}: {
  document: Document;
  selectedCriterionId?: string;
}) => {
  const remarks = useMemo(
    () =>
      document.suggestions
        .filter(
          (suggestion) =>
            suggestion.status === "approved" &&
            suggestion.rubricCriteriaId &&
            (!selectedCriterionId ||
              suggestion.rubricCriteriaId === selectedCriterionId)
        )
        .sort((a, b) => sortByFirstBlock(document, a, b)),
    [document, selectedCriterionId]
  );

  const sentimentBorder = (sentiment: string) => {
    switch (sentiment) {
      case "positive":
        return "border-positive bg-positive";
      case "negative":
        return "border-negative bg-negative";
      default:
        return "border-neutral bg-neutral";
    }
  };

  if (!selectedCriterionId) {
    return (
      <PanelEmptyState
        title="No selected rubric criterion"
        message="Click 'Show remarks' on a rubric criterion to view its remarks."
      />
    );
  }

  if (remarks.length === 0) {
    return (
      <PanelEmptyState
        title="No remarks yet"
        message="Approved rubric remarks will show up here while grading."
      />
    );
  }

  return (
    <div className="space-y-4">
      {remarks.map((suggestion) => {
        return (
          <div key={suggestion.id}>
            <Card
              className={cn(
                sentimentBorder(suggestion.sentiment),
                "border py-1 flex-1 shadow-none rounded-md"
              )}
            >
              <CardContent className="px-2">
                <div className="text-sm py-1">{suggestion.text}</div>
              </CardContent>
            </Card>
          </div>
        );
      })}
    </div>
  );
};

const sortByFirstBlock = (document: Document, a: Suggestion, b: Suggestion) => {
  const aFirstBlockIndex = document.blocks.findIndex(
    (block) => block.id === a.blocks[0]
  );
  const bFirstBlockIndex = document.blocks.findIndex(
    (block) => block.id === b.blocks[0]
  );
  return aFirstBlockIndex - bFirstBlockIndex;
};
